import { useCallback, useEffect, useState } from 'react';

import SystemFeatureService from '../../services/system/systemFeatureService';
import type { SystemFeatureMap } from '../../types/system/features';

/**
 * Caché a nivel de módulo: las funcionalidades del sistema cambian muy poco
 * (solo al cambiar de plan en configuración), así que se piden una sola vez
 * por sesión y se comparten entre todos los componentes.
 */
let cache: SystemFeatureMap | null = null;
let inflight: Promise<SystemFeatureMap> | null = null;
const listeners = new Set<(map: SystemFeatureMap) => void>();

const loadFeatures = (): Promise<SystemFeatureMap> => {
  if (cache) return Promise.resolve(cache);

  if (!inflight) {
    inflight = SystemFeatureService.fetchFeatures()
      .then((response) => {
        cache = response.data ?? {};
        listeners.forEach((listener) => listener(cache as SystemFeatureMap));
        return cache;
      })
      .catch(() => {
        // Ante un fallo no bloqueamos la UI: se asume sin funcionalidades
        // extra y se podrá reintentar en el próximo montaje.
        return {} as SystemFeatureMap;
      })
      .finally(() => {
        inflight = null;
      });
  }

  return inflight;
};

/** Fuerza una recarga (p. ej. tras cambiar el plan activo). */
export const invalidateSystemFeatures = () => {
  cache = null;
  inflight = null;
};

export const useSystemFeatures = () => {
  const [features, setFeatures] = useState<SystemFeatureMap>(cache ?? {});
  const [loaded, setLoaded] = useState<boolean>(cache != null);

  useEffect(() => {
    let cancelled = false;

    const listener = (map: SystemFeatureMap) => {
      if (cancelled) return;
      setFeatures(map);
      setLoaded(true);
    };
    listeners.add(listener);

    if (cache) {
      setFeatures(cache);
      setLoaded(true);
    } else {
      loadFeatures().then((map) => {
        if (cancelled) return;
        setFeatures(map);
        setLoaded(true);
      });
    }

    return () => {
      cancelled = true;
      listeners.delete(listener);
    };
  }, []);

  const isEnabled = useCallback(
    (code: string) => features[code] === true,
    [features]
  );

  return { features, loaded, isEnabled };
};

export default useSystemFeatures;
